import { z } from "zod";
/**
 * Brand ramp derivation. An admin picks one hex on the Branding page; the app
 * needs all eleven Tailwind shades plus a text color that stays readable on top
 * of the 600 stop.
 *
 * Pure and dependency-free apart from zod, so the API and the browser derive
 * identically and the math is unit-tested where this repo keeps its pure logic.
 */
/** The shades `tailwind.config.js` defines, light to dark. */
export const BRAND_STOPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];
const HEX_RE = /^#[0-9a-f]{6}$/i;
/** `null` means the app's own blue, not "unset". */
export const brandColorSchema = z
    .string()
    .trim()
    .toLowerCase()
    .regex(HEX_RE, "Use a six-digit hex color, like #0b6fc4")
    .nullable();
/**
 * The app's own accent: a saturated scoreboard blue. It sits 13 degrees off the
 * `navy` hue the header and hero are built from and carries far more saturation,
 * so a primary button or the logo plate reads as an accent on that navy rather
 * than a lighter patch of it.
 *
 * It also lands where the field green it replaced landed on every readability
 * measure the app depends on — white clears AA on the 600 stop (5.14:1, green
 * was 5.16:1) and the 700 stop is still the readable accent text — so no
 * component had to be re-tuned around the swap.
 */
export const DEFAULT_BRAND_SEED = "#0b6fc4";
/**
 * Exactly `deriveBrandRamp(DEFAULT_BRAND_SEED)`, written out so the browser has
 * the default before any JavaScript runs. `brandRamp.test.ts` asserts the two
 * are identical; an admin who types the default hex gets the default back.
 */
export const DEFAULT_BRAND_RAMP = {
    50: "241 248 254",
    100: "208 232 252",
    200: "164 211 250",
    300: "118 188 247",
    400: "66 163 244",
    500: "25 142 242",
    600: "11 111 196",
    700: "9 86 152",
    800: "7 70 123",
    900: "5 53 94",
    950: "2 25 43",
};
export const DEFAULT_BRAND_FOREGROUND = "255 255 255";
/** The blue ramp's 700 stop, which already clears AA on the app's light surfaces. */
export const DEFAULT_BRAND_ACCENT_TEXT = "9 86 152";
function parseHex(hex) {
    const value = hex.trim();
    if (!HEX_RE.test(value))
        throw new Error(`Not a six-digit hex color: ${hex}`);
    const n = Number.parseInt(value.slice(1), 16);
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}
function rgbToHsl({ r, g, b }) {
    const rn = r / 255;
    const gn = g / 255;
    const bn = b / 255;
    const max = Math.max(rn, gn, bn);
    const min = Math.min(rn, gn, bn);
    const l = (max + min) / 2;
    if (max === min)
        return { h: 0, s: 0, l };
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    const h = max === rn
        ? ((gn - bn) / d + (gn < bn ? 6 : 0)) * 60
        : max === gn
            ? ((bn - rn) / d + 2) * 60
            : ((rn - gn) / d + 4) * 60;
    return { h, s, l };
}
function hslToRgb({ h, s, l }) {
    if (s === 0) {
        const v = Math.round(l * 255);
        return { r: v, g: v, b: v };
    }
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;
    const [r1, g1, b1] = h < 60
        ? [c, x, 0]
        : h < 120
            ? [x, c, 0]
            : h < 180
                ? [0, c, x]
                : h < 240
                    ? [0, x, c]
                    : h < 300
                        ? [x, 0, c]
                        : [c, 0, x];
    return {
        r: Math.round((r1 + m) * 255),
        g: Math.round((g1 + m) * 255),
        b: Math.round((b1 + m) * 255),
    };
}
const triple = ({ r, g, b }) => `${r} ${g} ${b}`;
/** Exposed so callers can measure contrast against the color an admin typed. */
export function hexToChannels(hex) {
    return triple(parseHex(hex));
}
/* --------------------------------- the ramp -------------------------------- */
/**
 * Where each stop sits between the light end and the seed, and between the seed
 * and the dark end. Originally fitted to a hand-tuned ramp; now these fractions
 * are what defines the default, since `DEFAULT_BRAND_RAMP` is this function's
 * output for `DEFAULT_BRAND_SEED`. Moving one moves the app's own colors too.
 */
const LIGHT_STEPS = [
    [50, 0],
    [100, 0.12],
    [200, 0.28],
    [300, 0.45],
    [400, 0.64],
    [500, 0.79],
];
const DARK_STEPS = [
    [700, 0.29],
    [800, 0.48],
    [900, 0.67],
    [950, 1],
];
/**
 * A seed at the very top or bottom of the lightness range leaves no room for
 * six lighter stops (or four darker ones), so the ramp would flatten out. Pull
 * such a seed into a workable band; every realistic brand color is already
 * inside it and lands on 600 untouched.
 */
const MIN_SEED_L = 0.06;
const MAX_SEED_L = 0.94;
const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
/**
 * Eleven shades from one hex, holding the seed's hue and saturation and varying
 * only lightness. Constant saturation is a deliberate simplicity choice: it is
 * predictable, trivially testable, and the aesthetic cost at the extremes is
 * small.
 *
 * The seed lands exactly on 600 — the stop behind primary buttons, the logo
 * plate, and the diamond mark. Anything else there and "matches my brand" is
 * not true.
 *
 * @throws if `hex` is not `#rrggbb`.
 */
export function deriveBrandRamp(hex) {
    const seed = parseHex(hex);
    const { h, s, l } = rgbToHsl(seed);
    const anchor = clamp(l, MIN_SEED_L, MAX_SEED_L);
    const lightEnd = Math.min(1, Math.max(0.97, anchor + 0.05));
    const darkEnd = Math.max(0, Math.min(0.09, anchor - 0.05));
    const at = (lightness) => triple(hslToRgb({ h, s, l: lightness }));
    const ramp = {};
    for (const [stop, f] of LIGHT_STEPS)
        ramp[stop] = at(lightEnd - f * (lightEnd - anchor));
    // The seed itself, not a recomputed approximation of it — unless it was out
    // of band, in which case the clamped anchor is the closest usable color.
    ramp[600] = anchor === l ? triple(seed) : at(anchor);
    for (const [stop, f] of DARK_STEPS)
        ramp[stop] = at(anchor - f * (anchor - darkEnd));
    return ramp;
}
/* ------------------------------- readability ------------------------------- */
const channelLuminance = (c) => {
    const n = c / 255;
    return n <= 0.03928 ? n / 12.92 : ((n + 0.055) / 1.055) ** 2.4;
};
function channels(color) {
    const [r, g, b] = color.split(" ");
    return [Number(r), Number(g), Number(b)];
}
function relativeLuminance(color) {
    const [r, g, b] = channels(color);
    return 0.2126 * channelLuminance(r) + 0.7152 * channelLuminance(g) + 0.0722 * channelLuminance(b);
}
/** WCAG 2.1 contrast ratio between two channel triples, 1…21. */
export function contrastRatio(a, b) {
    const la = relativeLuminance(a);
    const lb = relativeLuminance(b);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}
const WCAG_AA = 4.5;
const WHITE = "255 255 255";
const BLACK = "0 0 0";
/**
 * Text that stays readable on `brand-600`. White wherever it clears WCAG AA,
 * otherwise the ramp's darkest stop — a near-black tinted with the brand's own
 * hue, which looks deliberate rather than stark.
 *
 * Pure black or white is the last resort so that there is always a defined
 * answer: a primary button can never render unreadable, whatever an admin
 * picks.
 */
export function readableForeground(hex) {
    const seed = hexToChannels(hex);
    if (contrastRatio(WHITE, seed) >= WCAG_AA)
        return WHITE;
    const darkest = deriveBrandRamp(hex)[950];
    if (contrastRatio(darkest, seed) >= WCAG_AA)
        return darkest;
    return contrastRatio(BLACK, seed) >= contrastRatio(WHITE, seed) ? BLACK : WHITE;
}
/** Stops dark enough to be worth trying as text, in order of preference. */
const ACCENT_TEXT_STOPS = [700, 800, 900, 950];
/**
 * The brand color used *as* text — link text, badge labels, the section
 * eyebrow — on the app's light surfaces.
 *
 * `brand-700` is the natural choice and is what a dark brand resolves to, but
 * on a light brand it is unreadable: `#fbbf24`'s 700 stop scores 2.5:1 on
 * white, well under WCAG AA. So walk down the ramp until a stop clears 4.5:1
 * against both surfaces this sits on — white and `brand-50` — and fall back to
 * the near-black 950, which always clears.
 *
 * Measured against the darker of the two backgrounds, since `brand-50` tints
 * toward the brand and is never quite white.
 */
export function readableAccentText(hex) {
    const ramp = deriveBrandRamp(hex);
    const surfaces = [WHITE, ramp[50]];
    const clears = (stop) => surfaces.every((surface) => contrastRatio(stop, surface) >= WCAG_AA);
    return ACCENT_TEXT_STOPS.map((stop) => ramp[stop]).find(clears) ?? ramp[950];
}
//# sourceMappingURL=brandRamp.js.map